import React, { useMemo, useState } from "react";
import { detectTextLanguage, translateText } from "../../utils/reviewTranslation";

export default function TranslatableText({
  text = "",
  label = "Review",
  bodyStyle = {},
  compact = false,
}) {
  const [translatedText, setTranslatedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [error, setError] = useState("");

  const originalText = String(text || "").trim();
  const detectedLanguage = useMemo(() => detectTextLanguage(originalText), [originalText]);
  const targetLanguage = detectedLanguage === "es" ? "en" : "es";
  const targetLabel = targetLanguage === "es" ? "Spanish" : "English";
  const hasText = originalText.length > 0;

  if (!hasText) return null;

  const handleTranslate = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await translateText(originalText, targetLanguage);
      setTranslatedText(result);
      setShowTranslation(true);
      setShowOriginal(true);
    } catch (translationError) {
      console.error("Translation failed:", translationError);
      setError("Translation is not available right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.shell}>
      {showOriginal ? (
        <div style={styles.block}>
          {showTranslation ? (
            <div style={styles.label}>Original {label}</div>
          ) : null}
          <div style={{ ...styles.body, ...bodyStyle }}>{originalText}</div>
        </div>
      ) : null}

      <div style={styles.actions}>
        <button
          type="button"
          style={{ ...styles.translateButton, padding: compact ? "7px 10px" : "8px 12px" }}
          onClick={handleTranslate}
          disabled={loading}
        >
          {loading ? "Translating..." : `Translate to ${targetLabel}`}
        </button>
        {showTranslation ? (
          <>
            <button type="button" style={styles.toggleButton} onClick={() => setShowOriginal((prev) => !prev)}>
              {showOriginal ? "Hide Original" : "Show Original"}
            </button>
            <button type="button" style={styles.toggleButton} onClick={() => setShowTranslation(false)}>
              Hide Translation
            </button>
          </>
        ) : null}
      </div>

      {showTranslation ? (
        <div style={styles.translationBlock}>
          <div style={styles.label}>Translated {label}</div>
          <div style={{ ...styles.body, ...bodyStyle }}>{translatedText}</div>
          <div style={styles.disclaimer}>Translations may not be perfectly accurate.</div>
        </div>
      ) : null}

      {error ? <div style={styles.error}>{error}</div> : null}
    </div>
  );
}

const styles = {
  shell: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "visible",
    display: "grid",
    gap: 8,
  },
  block: {
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  body: {
    fontSize: 15,
    lineHeight: 1.7,
    color: "#334155",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    wordBreak: "normal",
    minWidth: 0,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  translateButton: {
    flex: "1 1 140px",
    maxWidth: "100%",
    minHeight: 36,
    border: "1px solid rgba(59,130,246,0.24)",
    borderRadius: 999,
    background: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 900,
    fontSize: 12,
    padding: "8px 12px",
    cursor: "pointer",
    boxSizing: "border-box",
    whiteSpace: "normal",
    textAlign: "center",
    lineHeight: 1.2,
  },
  toggleButton: {
    flex: "1 1 120px",
    maxWidth: "100%",
    minHeight: 34,
    border: "1px solid rgba(226,232,240,0.95)",
    borderRadius: 999,
    background: "#ffffff",
    color: "#475569",
    fontWeight: 800,
    fontSize: 12,
    padding: "7px 10px",
    cursor: "pointer",
    boxSizing: "border-box",
    whiteSpace: "normal",
    textAlign: "center",
    lineHeight: 1.2,
  },
  translationBlock: {
    borderRadius: 14,
    border: "1px solid rgba(16,185,129,0.22)",
    background: "#ecfdf5",
    padding: 10,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  label: {
    fontSize: 11,
    color: "#047857",
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 4,
  },
  disclaimer: {
    marginTop: 8,
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.45,
  },
  error: {
    color: "#b91c1c",
    fontSize: 12,
    lineHeight: 1.45,
  },
};
